// components
import React from 'react';
import { MenuItem } from '@mui/material';

import GridPageAbstract from '../abstract/GridPageAbstract';
import { mergeHeadWithStorage } from '../../utils/gridHeadStorage';
import CreateTransactionDialog from '../dialogs/CreateTransactionDialog';
import Iconify from '../../components/iconify';
import i18n from '../../i18n';
import { canDecidePayin } from '../../utils/instanceStates';
import { formatCurrency } from '../../utils/formatNumber';
import { INSTANCE_STATE } from '../../constants/instanceState';

// Must match action-permissions.yml and co.itfed.spring.api.utils.ActionPermissions.
// Confirm and reject are separate permissions: confirming asserts money moved,
// rejecting only fails something already stuck.
const CONFIRM_ACTION = 'manual-payin-confirm';
const REJECT_ACTION = 'manual-payin-reject';

export default class PayinInstancesPage extends GridPageAbstract {
    constructor(props) {
        super(props);
        this.Name = 'payinInstances';
        this.Server = this.props.context.getState().server.payinInstances;
        this.MenuDisplayType = 'menu';
        // Created only from a merchant terminal's Create payment / Create payout
        // action, which goes through core's signed merchant API and therefore picks
        // a gateway, reserves a wallet and writes the event trail. The generic add
        // would INSERT a row directly — a transaction no gateway has heard of — so
        // the button is gone and permissions.yml denies create to every role,
        // admin included. Edit and delete too: an instance changes only through
        // the manual decision actions in its menu and, once decided, an appeal.
        this.disableAdd = true;
        this.disableGlobalEditor = true;
        this.disableGlobalDelete = true;
        this.searchFields = ['uuid', 'gatewayTransactionId', 'merchantTransactionId'];

        const defaultHead = [
            { id: 'uuid', label: 'uuid', alignRight: false, cell: 'Text', displayGrid: true, disableEditor: true },
            // required/format mirror PayinInstanceRequestDTO in the cabinet API:
            // merchantTerminalId, gatewayId and requestedAmount are @NotNull;
            // orderUuid is optional but typed java.util.UUID, so it is validated
            // for shape only when supplied.
            { id: 'orderUuid', format: 'uuid', group: 'identification', label: 'orderUuid', alignRight: false, cell: 'Text', displayGrid: true },
            {
                id: 'merchantTerminalId', group: 'routing',
                label: 'merchantTerminal',
                alignRight: false,
                cell: 'Select',
                from: 'publicMerchantTerminals',
                object: 'merchantTerminal',
                show: 'name',
                required: true,
                displayGrid: true
            },
            {
                id: 'gatewayId', required: true, group: 'routing',
                label: 'gateway',
                alignRight: false,
                cell: 'Select',
                from: 'publicGateways',
                object: 'gateway',
                show: 'name',
                displayGrid: true
            },
            // The gateway settings it was created with: its actions follow these,
            // not what the gateway links now. Shown from the row, so no list is
            // loaded for them. Gateway users never get them (permissions.config.js).
            {
                id: 'payinSettingsId', group: 'routing',
                label: 'payinSettings',
                alignRight: false,
                cell: 'Select',
                object: 'payinSettings',
                show: 'name',
                displayGrid: true,
                disableEditor: true
            },
            {
                id: 'payinCallbackSettingsId', group: 'routing',
                label: 'payinCallbackSettings',
                alignRight: false,
                cell: 'Select',
                object: 'payinCallbackSettings',
                show: 'name',
                displayGrid: true,
                disableEditor: true
            },
            {
                id: 'walletId', group: 'routing',
                label: 'wallet',
                alignRight: false,
                cell: 'Select',
                from: 'publicWallets',
                object: 'wallet',
                show: 'uuid',
                displayGrid: true
            },
            // Where the payer was told to pay, as core answered the merchant: the
            // wallet's requisites with the amount and deadline, or what the bank
            // returned. Fixed at creation, so a wallet edited since still shows
            // here as the payer saw it. Empty on a payin refused at init.
            { id: 'receiverInfo', group: 'routing', label: 'requisitesGiven', alignRight: false, cell: 'Json', displayGrid: true, disableEditor: true },
            { id: 'requestedAmount', group: 'amounts', label: 'requestedAmount', alignRight: true, cell: 'Amount', required: true, displayGrid: true },
            // Set by core on a gateway sharing wallets by amount: the exact amount
            // the payer must send, the requested one or a nudged one. Only exactly
            // it confirms the payin automatically.
            { id: 'payableAmount', group: 'amounts', label: 'payableAmount', alignRight: true, cell: 'Amount', blankWhenNull: true, displayGrid: true, disableEditor: true },
            { id: 'actualAmount', group: 'amounts', label: 'amount', alignRight: true, cell: 'Amount', displayGrid: true },
            { id: 'walletAmount', group: 'amounts', label: 'gatewayAmount', alignRight: true, cell: 'Amount', displayGrid: true },
            { id: 'conversionRate', group: 'amounts', label: 'conversionRate', alignRight: true, cell: 'Number', displayGrid: true },
            // Fixed by core when the instance is created: the gateway's balance
            // currency and the rate into it. The API ignores all three on a request.
            {
                id: 'balanceCurrencyId', group: 'amounts',
                label: 'balanceCurrency',
                alignRight: false,
                cell: 'Select',
                from: 'publicCurrencies',
                object: 'balanceCurrency',
                show: 'code',
                displayGrid: true,
                disableEditor: true
            },
            { id: 'balanceRate', group: 'amounts', label: 'balanceRate', alignRight: true, cell: 'Number', displayGrid: true, disableEditor: true },
            { id: 'balanceAmount', group: 'amounts', label: 'balanceAmount', alignRight: true, cell: 'Amount', currencyObject: 'balanceCurrency', displayGrid: true, disableEditor: true },
            {
                id: 'state',
                label: 'state',
                alignRight: false,
                cell: 'State',
                items: INSTANCE_STATE,
                displayGrid: true,
                disableEditor: true
            },
            { id: 'stateDesc', label: 'stateDesc', alignRight: false, cell: 'Text', displayGrid: true, disableEditor: true },
            { id: 'stateMessage', label: 'statusDesc', alignRight: false, cell: 'BigText', displayGrid: true, disableEditor: true },
            { id: 'internalStateDescription', label: 'internalStateDescription', alignRight: false, cell: 'BigText', humanizeDurations: true, displayGrid: true, disableEditor: true },
            // The appeal that last changed its outcome, set by core-cron when it
            // applies one. Gateway users never get it (permissions.config.js).
            { id: 'appealId', label: 'changedByAppeal', alignRight: true, cell: 'Number', prefix: '#', blankWhenNull: true, displayGrid: true, disableEditor: true },
            { id: 'clientIp', label: 'ip', alignRight: false, cell: 'Text', displayGrid: true, disableEditor: true },
            { id: 'payload', label: 'payload', alignRight: false, cell: 'Json', displayGrid: false, disableEditor: true },
            { id: 'callbackUrl', label: 'callbackUrl', alignRight: false, cell: 'Url', displayGrid: true, disableEditor: true },
            { id: 'gatewayTransactionId', label: 'gatewayTransactionId', alignRight: false, cell: 'Text', displayGrid: true, disableEditor: true },
            { id: 'merchantTransactionId', label: 'merchantTransaction', alignRight: false, cell: 'Text', displayGrid: true, disableEditor: true },
            { id: 'totalIncludedCommission', label: 'totalIncludedCommission', alignRight: true, cell: 'Amount', displayGrid: true, disableEditor: true },
            { id: 'totalOntopCommission', label: 'totalOntopCommission', alignRight: true, cell: 'Amount', displayGrid: true, disableEditor: true },
            { id: 'totalAgentCommission', label: 'totalAgentCommission', alignRight: true, cell: 'Amount', displayGrid: true, disableEditor: true },
            { id: 'commissionAgentJson', label: 'commissionAgentJson', alignRight: false, cell: 'Json', displayGrid: false, disableEditor: true },
            { id: 'commissionMerchantJson', label: 'commissionMerchantJson', alignRight: false, cell: 'Json', displayGrid: false, disableEditor: true },
            { id: 'commissionGwJson', label: 'commissionGwJson', alignRight: false, cell: 'Json', displayGrid: false, disableEditor: true },
            { id: 'isLocked', label: 'isLocked', alignRight: false, cell: 'Boolean', displayGrid: true, disableEditor: true },
            { id: 'isFinalized', label: 'isFinalized', alignRight: false, cell: 'Boolean', displayGrid: true, disableEditor: true },
            {
                id: 'dateCreatedModified',
                elements: ['dateCreated', 'dateModified'],
                label: 'dateCreatedModified',
                alignRight: false,
                cell: 'CreateModify',
                disableEditor: true,
                displayGrid: true,
                customSort: 'dateCreated'
            }
        ];
        this.Head = mergeHeadWithStorage(this.Name, defaultHead);

        this.state = {
            ...this.state,
            head: this.Head,
            decisionAction: null,
            decisionId: null,
            decisionRow: null
        };
    }

    openDecision(action) {
        // Read the row before closing the menu — handleCloseMenu nulls
        // menuClickItemId, so the other order loses it.
        const row = this.getMenuClickItem();
        this.handleCloseMenu();
        this.setState({ decisionAction: action, decisionId: row?.id, decisionRow: row });
    }

    closeDecision = () => {
        this.setState({ decisionAction: null, decisionId: null, decisionRow: null });
        // The row's state changed, so the grid behind the dialog is now stale.
        this.retrieveData();
    };

    renderCustomMenuItems(id) {
        const row = this.List.find((item) => item.id === id);
        // Nothing to decide on a payin that already finished — the API refuses
        // it, and offering the action would only produce an error. An expired
        // payin is the exception: its money can arrive late, so it may still be
        // confirmed (not rejected).
        const items = [];
        if (this.allowedAction(CONFIRM_ACTION) && canDecidePayin(row?.state, 'confirm')) {
            items.push(
                <MenuItem key={`${id}-confirm`} onClick={() => this.openDecision('confirm')}>
                    <Iconify icon="eva:checkmark-circle-2-outline" sx={{ mr: 2 }} />
                    {i18n.t('buttons.manualConfirm')}
                </MenuItem>
            );
        }
        if (this.allowedAction(REJECT_ACTION) && canDecidePayin(row?.state, 'reject')) {
            items.push(
                <MenuItem key={`${id}-reject`} onClick={() => this.openDecision('reject')}>
                    <Iconify icon="eva:close-circle-outline" sx={{ mr: 2 }} />
                    {i18n.t('buttons.manualReject')}
                </MenuItem>
            );
        }
        return items;
    }

    renderCustom() {
        const { decisionAction, decisionId, decisionRow } = this.state;
        if (!decisionAction || !decisionId) return null;

        const isConfirm = decisionAction === 'confirm';
        // The decision itself is the endpoint, so all the operator supplies is the
        // evidence for it: the reference or reason and, on a confirm, what arrived.
        const textField = {
            id: 'text',
            label: isConfirm ? 'reference' : 'reason',
            cell: 'BigText',
            required: true,
            helperText: isConfirm ? 'manualPayinReference' : 'manualRejectReason'
        };
        // Empty rather than prefilled with the requested amount: a payer can send
        // less, and a prefilled figure is one the operator confirms without reading
        // the statement. The requested amount is in the hint to compare against.
        const amountField = {
            id: 'amount',
            label: 'amountReceived',
            cell: 'Amount',
            required: true,
            // A payin that had to be paid exactly says so: on a wallet shared by
            // amount, a different figure may be another payin's money.
            helperText: decisionRow?.payableAmount != null ? 'manualPayinAmountExact' : 'manualPayinAmount',
            helperTextParams: {
                requested: formatCurrency(decisionRow?.requestedAmount ?? null, decisionRow?.currency),
                payable: formatCurrency(decisionRow?.payableAmount ?? null, decisionRow?.currency)
            }
        };

        return (
            <CreateTransactionDialog
                dialogOpen
                type={`payin-${decisionAction}`}
                id={decisionId}
                Head={isConfirm ? [amountField, textField] : [textField]}
                dialogTitle={isConfirm ? 'dialogs.manualConfirm' : 'dialogs.manualReject'}
                saveLabel={isConfirm ? 'buttons.manualConfirm' : 'buttons.manualReject'}
                // Never prefill a reference from a previous decision: it would
                // attach one payout's bank reference to another.
                enablePrevData={false}
                server={(id, body) =>
                    isConfirm
                        ? this.Server.confirm(id, body.text, body.amount)
                        : this.Server.reject(id, body.text)
                }
                onClose={this.closeDecision}
            />
        );
    }
}
